import { useEffect, useState, useRef, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import {
  ShieldCheck, UserPlus, PencilSimple, Trash, CircleNotch, X, MagnifyingGlass,
  Buildings, UsersThree, Check, WarningCircle,
} from "@phosphor-icons/react";

const ROLE_BADGE = {
  admin: "bg-[#0A0A0A] text-white",
  client: "bg-[#0055FF]/10 text-[#0055FF]",
  user: "bg-neutral-100 text-neutral-600",
};

function AccessEditor({ access, setAccess }) {
  const [groups, setGroups] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    api.get("/client-groups").then((r) => setGroups(r.data.groups)).catch(() => {});
  }, []);

  const toggleGroup = (g) => {
    const has = access.groups.some((x) => x.group_id === g.group_id);
    setAccess({
      ...access,
      groups: has ? access.groups.filter((x) => x.group_id !== g.group_id) : [...access.groups, { group_id: g.group_id, name: g.name }],
    });
  };

  const onQuery = (v) => {
    setQuery(v);
    clearTimeout(debounce.current);
    if (v.trim().length < 2) { setResults(null); return; }
    setSearching(true);
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/clients", { params: { search: v, limit: 8, page: 1 } });
        setResults(data.items);
      } finally { setSearching(false); }
    }, 300);
  };

  const addClient = (c) => {
    if (!access.clients.some((x) => x.id === c.id)) {
      setAccess({ ...access, clients: [...access.clients, { id: c.id, name: c.name }] });
    }
    setQuery(""); setResults(null);
  };
  const removeClient = (id) => setAccess({ ...access, clients: access.clients.filter((x) => x.id !== id) });

  return (
    <div className="space-y-4" data-testid="access-editor">
      <div>
        <label className="mb-1.5 block text-sm font-semibold text-[#0A0A0A]">Client Groups</label>
        {groups.length === 0 ? (
          <p className="text-sm text-neutral-500">No client groups exist yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {groups.map((g) => {
              const sel = access.groups.some((x) => x.group_id === g.group_id);
              return (
                <button type="button" key={g.group_id} data-testid={`access-group-${g.group_id}`} onClick={() => toggleGroup(g)}
                  className={`flex items-center gap-1.5 rounded-sm border px-3 py-1.5 text-sm font-medium transition-all duration-200 ${sel ? "border-[#0055FF] bg-[#0055FF]/[0.06] text-[#0055FF]" : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"}`}>
                  {sel ? <Check size={14} weight="bold" /> : <UsersThree size={14} />} {g.name} <span className="text-xs text-neutral-400">({g.client_count})</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-semibold text-[#0A0A0A]">Individual Clients</label>
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input data-testid="access-client-search" value={query} onChange={(e) => onQuery(e.target.value)} placeholder="Search clients to add…"
            className="w-full rounded-sm border border-neutral-300 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-[#0055FF]" />
          {results && (
            <ul className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-sm border border-neutral-200 bg-white shadow-lg" data-testid="access-client-results">
              {searching && <li className="px-3 py-2 text-sm text-neutral-500">Searching…</li>}
              {!searching && results.length === 0 && <li className="px-3 py-2 text-sm text-neutral-500">No matches.</li>}
              {results.map((c) => (
                <li key={c.id}>
                  <button type="button" onClick={() => addClient(c)} data-testid={`access-add-client-${c.id}`}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#0055FF]/5">
                    <Buildings size={14} className="text-neutral-400" /> <span className="truncate">{c.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {access.clients.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {access.clients.map((c) => (
              <span key={c.id} className="flex items-center gap-1.5 rounded-sm bg-neutral-100 px-2.5 py-1 text-sm" data-testid={`access-client-chip-${c.id}`}>
                {c.name}
                <button type="button" onClick={() => removeClient(c.id)} className="text-neutral-400 hover:text-[#DC2626]"><X size={13} weight="bold" /></button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const EMPTY_FORM = { email: "", password: "", name: "", role: "client", access: { clients: [], groups: [] } };

function UserModal({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(editing
    ? { email: editing.email, password: "", name: editing.name, role: editing.role, access: editing.access || { clients: [], groups: [] } }
    : EMPTY_FORM);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      if (editing) {
        const body = { name: form.name, role: form.role, access: form.access };
        if (form.password) body.password = form.password;
        await api.patch(`/admin/users/${editing.user_id}`, body);
      } else {
        await api.post("/admin/users", form);
      }
      onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8" data-testid="user-modal">
      <div className="my-auto w-full max-w-lg rounded-sm border border-neutral-200 bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
          <h2 className="font-heading text-xl font-black tracking-tighter">{editing ? "Edit user" : "New login"}</h2>
          <button onClick={onClose} data-testid="user-modal-close" className="text-neutral-400 hover:text-[#0A0A0A]"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4 px-6 py-5">
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-[#0A0A0A]">Full name</label>
            <input data-testid="user-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-sm border border-neutral-300 bg-white px-3.5 py-2.5 text-base outline-none focus:border-[#0055FF]" placeholder="Jane Smith" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-[#0A0A0A]">Email</label>
            <input data-testid="user-email-input" type="email" required disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full rounded-sm border border-neutral-300 bg-white px-3.5 py-2.5 text-base outline-none focus:border-[#0055FF] disabled:bg-neutral-100 disabled:text-neutral-500" placeholder="you@company.com" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-[#0A0A0A]">{editing ? "New password (leave blank to keep)" : "Password"}</label>
            <input data-testid="user-password-input" type="text" required={!editing} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full rounded-sm border border-neutral-300 bg-white px-3.5 py-2.5 text-base outline-none focus:border-[#0055FF]" placeholder="Set a password to share" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-[#0A0A0A]">Role</label>
            <div className="flex gap-2">
              {["client", "admin"].map((r) => (
                <button type="button" key={r} data-testid={`role-${r}`} onClick={() => setForm({ ...form, role: r })}
                  className={`flex-1 rounded-sm border px-3 py-2 text-sm font-semibold capitalize transition-all duration-200 ${form.role === r ? "border-[#0055FF] bg-[#0055FF]/[0.06] text-[#0055FF]" : "border-neutral-300 bg-white text-neutral-700"}`}>
                  {r === "admin" ? "Admin (full access)" : "Client (scoped access)"}
                </button>
              ))}
            </div>
          </div>

          {form.role === "client" && (
            <div className="border-t border-neutral-200 pt-4">
              <p className="mb-3 text-sm text-neutral-600">Choose which clients and groups this login can view in Asset Intelligence.</p>
              <AccessEditor access={form.access} setAccess={(a) => setForm({ ...form, access: a })} />
            </div>
          )}

          {error && <div data-testid="user-modal-error" className="rounded-sm border border-[#DC2626]/30 bg-[#DC2626]/5 px-3.5 py-2.5 text-sm text-[#DC2626]">{error}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-sm border border-neutral-300 bg-white px-4 py-2.5 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">Cancel</button>
            <button type="submit" data-testid="user-modal-save" disabled={saving}
              className="flex items-center gap-2 rounded-sm bg-[#0055FF] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#0044CC] disabled:opacity-50">
              {saving && <CircleNotch size={16} className="animate-spin" />} {editing ? "Save changes" : "Create login"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [modal, setModal] = useState(null); // {editing?} | null
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doDelete = async (u) => {
    setError("");
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setConfirmDelete(null);
    }
  };

  const accessSummary = (u) => {
    if (u.role !== "client") return "—";
    const g = u.access?.groups?.length || 0;
    const c = u.access?.clients?.length || 0;
    if (!g && !c) return "No access assigned";
    return [g ? `${g} group${g > 1 ? "s" : ""}` : null, c ? `${c} client${c > 1 ? "s" : ""}` : null].filter(Boolean).join(", ");
  };

  return (
    <Layout>
      <div data-testid="admin-users-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Administration</p>
            <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">User Management</h1>
            <p className="mt-2 max-w-2xl text-base text-neutral-600">Create admin logins and scoped client logins. Client logins only see the Asset Intelligence for the clients and groups you assign.</p>
          </div>
          <button data-testid="new-user-button" onClick={() => setModal({})}
            className="flex items-center gap-2 rounded-sm bg-[#0055FF] px-5 py-2.5 text-base font-semibold text-white transition-all duration-200 hover:bg-[#0044CC]">
            <UserPlus size={18} weight="bold" /> New login
          </button>
        </div>

        {error && <div data-testid="admin-error" className="mt-4 rounded-sm border border-[#DC2626]/30 bg-[#DC2626]/5 px-4 py-3 text-sm text-[#DC2626]">{error}</div>}

        <div className="mt-6 border border-neutral-200 bg-white shadow-sm rounded-sm">
          {users === null ? (
            <div className="flex h-40 items-center justify-center"><CircleNotch size={26} className="animate-spin text-[#0055FF]" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead><tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                  <th className="px-5 py-3 text-left">Name</th><th className="px-5 py-3 text-left">Email</th>
                  <th className="px-5 py-3 text-left">Role</th><th className="px-5 py-3 text-left">Access</th>
                  <th className="px-5 py-3 text-left">Sign-in</th><th className="px-5 py-3 text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-b border-neutral-100 even:bg-neutral-50/60" data-testid={`user-row-${u.email}`}>
                      <td className="px-5 py-3 font-semibold text-[#0A0A0A]">{u.name || "—"}</td>
                      <td className="px-5 py-3 text-neutral-600">{u.email}</td>
                      <td className="px-5 py-3"><span className={`rounded-sm px-2 py-0.5 text-xs font-semibold capitalize ${ROLE_BADGE[u.role] || ROLE_BADGE.user}`}>{u.role}</span></td>
                      <td className="px-5 py-3 text-neutral-600">{accessSummary(u)}</td>
                      <td className="px-5 py-3 text-neutral-500 capitalize">{u.auth_provider}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <button data-testid={`edit-user-${u.email}`} onClick={() => setModal({ editing: u })} className="rounded-sm p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-[#0055FF]"><PencilSimple size={16} /></button>
                          <button data-testid={`delete-user-${u.email}`} onClick={() => setConfirmDelete(u)} className="rounded-sm p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-[#DC2626]"><Trash size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modal && <UserModal editing={modal.editing} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="delete-modal">
          <div className="w-full max-w-sm rounded-sm border border-neutral-200 bg-white p-6 shadow-xl text-center">
            <WarningCircle size={32} weight="fill" className="mx-auto mb-3 text-[#DC2626]" />
            <p className="font-heading text-lg font-black tracking-tight">Delete {confirmDelete.name || confirmDelete.email}?</p>
            <p className="mt-1 text-sm text-neutral-600">This permanently removes their login.</p>
            <div className="mt-5 flex justify-center gap-2">
              <button onClick={() => setConfirmDelete(null)} className="rounded-sm border border-neutral-300 bg-white px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">Cancel</button>
              <button data-testid="confirm-delete" onClick={() => doDelete(confirmDelete)} className="rounded-sm bg-[#DC2626] px-4 py-2 text-sm font-semibold text-white hover:bg-[#B91C1C]">Delete</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
